// Rebrands user-facing copy at runtime, in every locale:
// - The upstream product name is replaced with the installation name. Only the
//   capitalised whole word is matched, so lowercase identifiers
//   (`chatwootSettings`) and compound names (`ChatwootApp`) stay intact, and a
//   word that is part of a path or host (`/Chatwoot`, `Chatwoot.io`) is skipped.
// - Links to the upstream vendor's sites point to the configured terms or
//   privacy page, or otherwise lose their link and keep only their text. Any
//   other mention of those hosts (example URLs) becomes `example.com`.
// Lookbehind is avoided so the widget and survey bundles still parse on older
// Safari.
const UPSTREAM_NAME_PATTERN = /\bChatwoot\b(?![./-]\w)/g;
const PATH_OR_HOST_PREFIX = /[/.@-]/;

const UPSTREAM_HOST = /\b(?:chatwoot\.com|chwt\.app)\b/i;
const UPSTREAM_HOST_GLOBAL = new RegExp(UPSTREAM_HOST.source, 'gi');
// Trailing sentence punctuation is not part of the URL.
const UPSTREAM_URL_PATTERN =
  /https?:\/\/(?:[\w-]+\.)*(?:chatwoot\.com|chwt\.app)\b(?:[^\s"'<>]*[^\s"'<>.,;:!?)])?/gi;
const ANCHOR_PATTERN =
  /<a\b([^>]*?)\bhref=(["'])([^"']*)\2([^>]*)>([\s\S]*?)<\/a>/gi;
const NEUTRAL_HOST = 'example.com';

// Characters with a meaning in vue-i18n message syntax are wrapped in literal
// interpolations so a name such as "A | B" is not read as a plural form.
const I18N_SPECIAL_CHARS = /[{}@|]/g;
const escapeForI18n = value =>
  value.replace(I18N_SPECIAL_CHARS, char => `{'${char}'}`);

const HTML_ATTRIBUTE_CHARS = /[&"'<>]/g;
const escapeForAttribute = value =>
  value.replace(HTML_ATTRIBUTE_CHARS, char => `&#${char.charCodeAt(0)};`);

const replaceUpstreamName = (text, name) =>
  text.replace(UPSTREAM_NAME_PATTERN, (match, offset) =>
    PATH_OR_HOST_PREFIX.test(text.charAt(offset - 1)) ? match : name
  );

const policyUrl = (url, { termsUrl, privacyUrl }) => {
  if (/terms/i.test(url)) return termsUrl;
  if (/privacy/i.test(url)) return privacyUrl;
  return '';
};

const replaceUpstreamLinks = (text, urls) =>
  text
    .replace(ANCHOR_PATTERN, (anchor, before, quote, href, after, label) => {
      if (!UPSTREAM_HOST.test(href)) return anchor;
      const target = policyUrl(href, urls);
      if (!target) return label;
      const escapedTarget = escapeForI18n(escapeForAttribute(target));
      return `<a${before}href=${quote}${escapedTarget}${quote}${after}>${label}</a>`;
    })
    .replace(UPSTREAM_URL_PATTERN, url => {
      const target = policyUrl(url, urls);
      return target ? escapeForI18n(target) : NEUTRAL_HOST;
    })
    .replace(UPSTREAM_HOST_GLOBAL, NEUTRAL_HOST);

const brandString = (text, { name, urls }) => {
  const unlinked = UPSTREAM_HOST.test(text)
    ? replaceUpstreamLinks(text, urls)
    : text;
  return name ? replaceUpstreamName(unlinked, name) : unlinked;
};

const brandValue = (value, brand) => {
  if (typeof value === 'string') return brandString(value, brand);
  if (Array.isArray(value)) return value.map(item => brandValue(item, brand));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, brandValue(item, brand)])
    );
  }
  return value;
};

// Returns a rebranded copy of a vue-i18n messages object (all locales), using
// INSTALLATION_NAME, TERMS_URL and PRIVACY_URL from the global config.
export const brandMessages = (messages, config = window.globalConfig || {}) =>
  brandValue(messages, {
    name: config.INSTALLATION_NAME && escapeForI18n(config.INSTALLATION_NAME),
    urls: { termsUrl: config.TERMS_URL, privacyUrl: config.PRIVACY_URL },
  });
